import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import useAiQuota from '@/hooks/useAiQuota';
import {
  User, MapPin, Calendar, Stethoscope, BookOpen, ExternalLink,
  Plus, Trash2, Save, Loader2, CheckCircle, XCircle, Clock, AlertCircle, ToggleRight, Star, Sparkles,
  Camera, Upload, Wallet, UserX, Crown, CreditCard, Download, Lock, AlertTriangle, Copy, Share2, MessageCircle,
  FileText, Image, Send, SpellCheck, Bell, BellOff, BarChart3, TrendingUp, Eye, EyeOff, Phone, Search,
  Newspaper, History, Check, X, Edit3, RefreshCw, ChevronDown, QrCode, Zap, Gift, Ticket, ClipboardList
} from 'lucide-react';
import BookingChat from '@/components/BookingChat';
import PinSettings from '@/components/dashboard/PinSettings';
import ProfilePublishedCelebration from '@/components/dashboard/ProfilePublishedCelebration';
import MonogramAvatar from '@/components/MonogramAvatar';
import VoiceMeter from '@/components/dashboard/VoiceMeter';
import ClinicSearchPicker from '@/components/ClinicSearchPicker';
import ClinicMap from '@/components/ClinicMap';
import HospitalAttachmentsSection from '@/components/dashboard/HospitalAttachmentsSection';
import {
  Field, ShareUrlBar, PushNotificationToggle, RedeemCouponCard,
  VoiceAuthenticityNotice, ChangePasswordSection,
  WhatsNewLaunchBanner, SetupBlocker, WebsiteScoreWidget,
  TierBadgeWithUpgrade, UpgradeBanner, SubscriptionWarning,
} from '@/components/dashboard/widgets/SharedWidgets';

export default function TqsTab() {
  const [data, setData] = useState(null);
  const [tracker, setTracker] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [pName, setPName] = useState('');
  const [pPhone, setPPhone] = useState('');
  const [pDate, setPDate] = useState('');
  const [generated, setGenerated] = useState(null);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [aggRes, trackerRes] = await Promise.all([
        api.get('/dashboard/tqs/aggregate'),
        api.get('/dashboard/tqs/tracker').catch(() => ({ data: { rows: [], summary: {} } })),
      ]);
      setData(aggRes.data);
      setTracker(trackerRes.data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendSurvey = async (e) => {
    e.preventDefault();
    setSending(true); setErr(''); setGenerated(null);
    try {
      const { data: d } = await api.post('/dashboard/tqs/send', {
        patient_name: pName.trim() || null,
        patient_phone: pPhone.trim() || null,
        appointment_date: pDate || null,
      });
      const url = `${window.location.origin}${d.survey_url}`;
      setGenerated({ url, name: pName });
      setPName(''); setPPhone(''); setPDate('');
      load();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || 'Could not generate link');
    } finally { setSending(false); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => alert('Link copied'));
  };

  // Connect-page share helpers (doctor's own /c/:slug acquisition link)
  const [connectShare, setConnectShare] = useState(null);
  useEffect(() => {
    api.post('/dashboard/connect-link').then(r => setConnectShare(r.data)).catch(() => {});
  }, []);
  const connectUrl = connectShare ? `${window.location.origin}${connectShare.connect_path}` : '';

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-sky-600 mx-auto" /></div>;
  const agg = data?.agg || {};
  const stars = agg.star_rating || 0;
  const recent = data?.recent || [];

  return (
    <div className="space-y-5" data-testid="tqs-tab">
      {/* Score header */}
      <div className="bg-gradient-to-br from-sky-50 via-violet-50 to-emerald-50 border border-sky-200 rounded-2xl p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[10px] font-bold text-sky-700 uppercase tracking-widest">DocSite Treatment Quality Score</p>
            <h3 className="text-xl md:text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {agg.count > 0 ? `${agg.avg_score} / 100` : 'No responses yet'}
            </h3>
            <div className="flex items-center gap-1 mt-1">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={16} className={i <= Math.floor(stars) ? 'text-amber-500 fill-amber-500' : i - 0.5 <= stars ? 'text-amber-500 fill-amber-500/50' : 'text-slate-300'} />
              ))}
              <span className="text-xs text-slate-600 ml-2">{agg.count || 0} response{agg.count === 1 ? '' : 's'}</span>
            </div>
          </div>
          <button onClick={() => setShowSend(s => !s)}
            className="rounded-full px-5 py-2.5 bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 flex items-center gap-2 shrink-0"
            data-testid="tqs-send-btn">
            <Send size={14} /> {showSend ? 'Close' : 'Send Survey to a Patient'}
          </button>
        </div>
        {agg.count > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5">
            {[
              ['Heard fully', agg.q1_yes_pct],
              ['Examined + explained', agg.q2_yes_pct],
              ['Clear next steps', agg.q3_yes_pct],
              ['Treatment worked', agg.q4_yes_pct],
            ].map(([label, pct]) => (
              <div key={label} className="bg-white rounded-xl border border-slate-200 p-3">
                <p className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">{label}</p>
                <p className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{pct ?? 0}<span className="text-xs text-slate-400">%</span></p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Connect link — doctor's frictionless patient-acquisition URL */}
      {connectShare && (
        <div className="bg-gradient-to-r from-violet-50 via-sky-50 to-emerald-50 border border-violet-200 rounded-2xl p-5" data-testid="connect-share-card">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-violet-600" />
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Your patient Connect link</p>
          </div>
          <p className="text-xs text-slate-600 mb-3">Share this single URL on WhatsApp / Status / clinic poster. Patients enter Name + Mobile + which clinic, and they're auto-added to your directory — no signup required. They also get instant feedback + booking + WhatsApp links to you.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input value={connectUrl} readOnly className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-mono outline-none" data-testid="connect-share-url" />
            <button onClick={() => copyLink(connectUrl)} className="rounded-xl px-4 py-2 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800" data-testid="connect-share-copy">Copy</button>
            <a href={`https://wa.me/?text=${encodeURIComponent(`${connectShare.share_message}\n\n${connectUrl}`)}`}
              target="_blank" rel="noopener noreferrer"
              className="rounded-xl px-4 py-2 bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 flex items-center justify-center gap-1.5" data-testid="connect-share-whatsapp">
              <MessageCircle size={12} /> WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Send survey form */}
      {showSend && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="tqs-send-form">
          <p className="text-sm font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Send a 4-question quality survey</p>
          <p className="text-xs text-slate-500 mb-4">Generates a private link you can share via WhatsApp / SMS / email. Best sent ~7 days after the appointment so patients can answer Q4 (did the treatment work).</p>
          <form onSubmit={sendSurvey} className="grid md:grid-cols-3 gap-3">
            <input value={pName} onChange={e => setPName(e.target.value)} placeholder="Patient name (optional)"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" data-testid="tqs-name-input" />
            <input value={pPhone} onChange={e => setPPhone(e.target.value)} placeholder="Patient phone (optional)"
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" data-testid="tqs-phone-input" />
            <input type="date" value={pDate} onChange={e => setPDate(e.target.value)}
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" data-testid="tqs-date-input" />
            <div className="md:col-span-3">
              <button type="submit" disabled={sending}
                className="rounded-full px-5 py-2 bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="tqs-generate-btn">
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Generate Survey Link
              </button>
            </div>
          </form>
          {generated && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="tqs-generated">
              <p className="text-xs font-semibold text-emerald-800 mb-1">Survey link ready{generated.name ? ` for ${generated.name}` : ''}:</p>
              <div className="flex items-center gap-2">
                <input value={generated.url} readOnly className="flex-1 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-mono outline-none" data-testid="tqs-generated-url" />
                <button onClick={() => copyLink(generated.url)} className="rounded-lg px-3 py-1.5 bg-slate-900 text-white text-xs font-medium hover:bg-slate-800">Copy</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Hi! Could you spend 60 seconds giving me honest feedback on your treatment? ${generated.url}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="rounded-lg px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700">WhatsApp</a>
              </div>
            </div>
          )}
          {err && <p className="text-xs text-rose-600 mt-2">{err}</p>}
        </div>
      )}

      {/* ─── Patient response tracker (Q1/Q2/Q3) ─────────────────────── */}
      {/*
        Horizontal row-per-patient grid. The doctor's mental model is
        "everyone said Yes by default — show me the patients who said No
        and on which question". Empty/Yes cells render as a subtle ✓; No
        cells render as a bold red ✕ so the doctor can scan a column in 2
        seconds. Q4 (outcome) sits in its own column with a hourglass when
        the reminder window is still open. */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="tqs-tracker">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Patient response tracker · Q1–Q3</p>
            <p className="text-[11px] text-slate-500 mt-0.5">Default assumption is <b>Yes</b> on every question. Red ✕ flags a patient's <b>No</b> answer — the cells you actually need to learn from.</p>
          </div>
          {tracker?.summary?.total > 0 && (
            <div className="flex items-center gap-1 text-[10px]">
              <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-bold">Q1 No · {tracker.summary.q1_no_count || 0}</span>
              <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-bold">Q2 No · {tracker.summary.q2_no_count || 0}</span>
              <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 font-bold">Q3 No · {tracker.summary.q3_no_count || 0}</span>
              {tracker.summary.outcome_pending > 0 && (
                <span className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-bold">Q4 pending · {tracker.summary.outcome_pending}</span>
              )}
            </div>
          )}
        </div>
        {(!tracker?.rows || tracker.rows.length === 0) ? (
          <p className="text-xs text-slate-500 py-4 text-center">No patient responses yet. Once Q1–Q3 come in from clinic check-ins, they'll appear here.</p>
        ) : (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs border-separate border-spacing-0" data-testid="tqs-tracker-table">
              <thead>
                <tr className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="text-left py-2 px-2 border-b border-slate-200">Patient</th>
                  <th className="text-left py-2 px-2 border-b border-slate-200">Visit</th>
                  <th className="text-center py-2 px-2 border-b border-slate-200" title="Heard fully">Q1</th>
                  <th className="text-center py-2 px-2 border-b border-slate-200" title="Examined + explained">Q2</th>
                  <th className="text-center py-2 px-2 border-b border-slate-200" title="Clear next steps">Q3</th>
                  <th className="text-center py-2 px-2 border-b border-slate-200" title="Treatment outcome">Q4</th>
                  <th className="text-right py-2 px-2 border-b border-slate-200">Score</th>
                </tr>
              </thead>
              <tbody>
                {tracker.rows.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`tqs-tracker-row-${i}`}>
                    <td className="py-2 px-2 border-b border-slate-100 font-medium text-slate-800 truncate max-w-[180px]" title={r.patient_name}>{r.patient_name}</td>
                    <td className="py-2 px-2 border-b border-slate-100 text-slate-500 text-[10px] whitespace-nowrap">
                      {r.appointment_date ? new Date(r.appointment_date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—'}
                    </td>
                    <TqsCell no={r.q1_no} testid={`tqs-tracker-q1-${i}`} />
                    <TqsCell no={r.q2_no} testid={`tqs-tracker-q2-${i}`} />
                    <TqsCell no={r.q3_no} testid={`tqs-tracker-q3-${i}`} />
                    <td className="py-2 px-2 border-b border-slate-100 text-center" data-testid={`tqs-tracker-q4-${i}`}>
                      {r.outcome_at == null && r.outcome_pending ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-50 text-amber-600 border border-amber-200" title={r.outcome_reminder_sent ? 'Reminder sent — awaiting patient response' : 'Outcome window opens day 7 — awaiting reminder'}>
                          <Clock size={11} />
                        </span>
                      ) : r.q4 === false ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 font-bold" title="Patient said the treatment did not work as expected">
                          <X size={11} />
                        </span>
                      ) : r.q4 === true ? (
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100" title="Treatment worked as expected">
                          <Check size={11} />
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 border-b border-slate-100 text-right font-bold text-slate-700 tabular-nums">
                      {r.score != null ? <>{r.score}<span className="text-slate-400 font-normal">/100</span></> : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent responses */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent responses ({recent.length})</p>
        {recent.length === 0 ? (
          <p className="text-xs text-slate-500">No completed surveys yet. Send your first one above — patients respond at ~70% rate when sent within 7 days of treatment.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((r, i) => (
              <li key={i} className="py-3 flex items-center justify-between gap-3" data-testid={`tqs-recent-${i}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900">{r.patient_name || 'Anonymous patient'}</p>
                  <p className="text-[10px] text-slate-500">{r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}</p>
                  {r.note && <p className="text-xs text-slate-600 mt-1 italic">"{r.note}"</p>}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.score}<span className="text-xs text-slate-400">/100</span></p>
                  <div className="flex gap-0.5 justify-end">
                    {[r.q1, r.q2, r.q3, r.q4].map((v, j) => (
                      <span key={j} className={`w-2 h-2 rounded-full ${v ? 'bg-emerald-500' : 'bg-rose-300'}`} title={`Q${j+1}: ${v ? 'Yes' : 'No'}`} />
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// Cell-level renderer for the Q1/Q2/Q3 tracker. Only `No` answers render
// loudly — `Yes` (default) shows a subtle tick so the doctor's eye is
// drawn exclusively to the No columns/rows that need attention.
function TqsCell({ no, testid }) {
  if (no) {
    return (
      <td className="py-2 px-2 border-b border-slate-100 text-center" data-testid={testid}>
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-rose-100 text-rose-700 border border-rose-300 font-bold shadow-sm" title="Patient answered No">
          <X size={11} strokeWidth={3} />
        </span>
      </td>
    );
  }
  return (
    <td className="py-2 px-2 border-b border-slate-100 text-center" data-testid={testid}>
      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50/60 text-emerald-500 border border-emerald-100" title="Yes (default)">
        <Check size={11} />
      </span>
    </td>
  );
}


